// DatePicker.tsx
import React, { useEffect, useState } from "react";
import "./DatePicker.css";
import { ReactComponent as CalendarIcon } from "common/assets/icons/calendar.svg";
import { ReactComponent as LeftIcon } from "common/assets/icons/chevron-left.svg";
import { ReactComponent as RightIcon } from "common/assets/icons/chevron-right.svg";
import { P } from "common/P/P";

interface DatePickerProps {
	label?: string;
	value?: string;
	onDateChange?: (date: string) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
	label,
	onDateChange,
	value,
}) => {
	const today = new Date();
	const [currentMonth, setCurrentMonth] = useState(today.getMonth());
	const [currentYear, setCurrentYear] = useState(today.getFullYear());
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [showDatePicker, setShowDatePicker] = useState(false);

	const MONTH_NAMES = [
		"Ýanwar",
		"Fewral",
		"Mart",
		"Aprel",
		"Maý",
		"Iýun",
		"Iýul",
		"Awgust",
		"Sentýabr",
		"Oktýabr",
		"Noýabr",
		"Dekabr",
	];
	const DAYS = ["Ýek", "Duş", "Siş", "Çar", "Pen", "Anna", "Şen"];

	useEffect(() => {
		if (value) {
			const dateParts = value.split("-").map((part) => parseInt(part));
			setSelectedDate(new Date(dateParts[0], dateParts[1] - 1, dateParts[2]));
		} else {
			setSelectedDate(null);
		}
	}, [value]);

	const getNoOfDays = () => {
		return new Date(currentYear, currentMonth + 1, 0).getDate();
	};

	const getBlankDays = () => {
		return new Date(currentYear, currentMonth).getDay();
	};

	const handlePrevMonth = () => {
		setCurrentMonth((prev) => (prev === 0 ? 11 : prev - 1));
		if (currentMonth === 0) {
			setCurrentYear((prev) => prev - 1);
		}
	};

	const handleNextMonth = () => {
		setCurrentMonth((prev) => (prev === 11 ? 0 : prev + 1));
		if (currentMonth === 11) {
			setCurrentYear((prev) => prev + 1);
		}
	};

	const formatDate = (date: Date) => {
		const day = date.getDate().toString().padStart(2, "0");
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	};

	const handleDateSelect = (day: number) => {
		const newDate = new Date(currentYear, currentMonth, day);
		setSelectedDate(newDate);
		setShowDatePicker(false);
		const isoFormattedDate = `${newDate.getFullYear()}-${(
			newDate.getMonth() + 1
		)
			.toString()
			.padStart(2, "0")}-${newDate.getDate().toString().padStart(2, "0")}`;
		onDateChange && onDateChange(isoFormattedDate); // Pass date in YYYY-MM-DD format
	};

	const daysArray = Array.from({ length: getNoOfDays() }, (_, i) => i + 1);
	const blankDaysArray = Array.from({ length: getBlankDays() }, () => "");

	return (
		<div className="grid w-full grid-cols-1 gap-2">
			{label && (
				<label>
					<P size="t1" type="regular" className="text-textColor">
						{label}
					</P>
				</label>
			)}
			<div className="relative">
				<input
					type="text"
					readOnly
					className="w-full py-3 pl-4 pr-10 font-medium leading-none text-gray-600 border rounded-md shadow-sm cursor-pointer input focus:outline-none focus:shadow-outline border-strokeColor"
					placeholder="DD/MM/YYYY"
					value={selectedDate ? formatDate(selectedDate) : ""}
					onClick={() => setShowDatePicker(!showDatePicker)}
				/>
				<div className="absolute top-0 right-0 px-3 py-3">
					<CalendarIcon />
				</div>

				{showDatePicker && (
					<div
						className="absolute top-0 left-0 z-10 p-4 mt-12 bg-white rounded-lg shadow"
						style={{ width: "17rem" }}
					>
						<div className="flex items-center justify-between mb-2">
							<div>
								<span className="text-lg font-bold text-gray-800">
									{MONTH_NAMES[currentMonth]}
								</span>
								<span className="ml-1 text-lg font-normal text-gray-600">
									{currentYear}
								</span>
							</div>
							<div>
								<LeftIcon
									className="inline-flex w-6 h-6 p-1 transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-lowOpacity stroke-textColor hover:stroke-mainColor"
									onClick={handlePrevMonth}
								/>

								<RightIcon
									className="inline-flex w-6 h-6 p-1 transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-lowOpacity stroke-textColor hover:stroke-mainColor"
									onClick={handleNextMonth}
								/>
							</div>
						</div>

						<div className="flex flex-wrap mb-3 -mx-1">
							{DAYS.map((day, index) => (
								<div key={index} style={{ width: "14.26%" }} className="px-1">
									<div className="text-xs font-medium text-center text-gray-800">
										{day}
									</div>
								</div>
							))}
						</div>

						<div className="flex flex-wrap -mx-1">
							{blankDaysArray.map((_, index) => (
								<div
									key={index}
									style={{ width: "14.28%" }}
									className="p-1 text-sm text-center border border-transparent"
								></div>
							))}
							{daysArray.map((day, index) => (
								<div
									key={index}
									style={{ width: "14.28%" }}
									className="px-1 mb-1"
								>
									<div
										onClick={() => handleDateSelect(day)}
										className="text-sm leading-none leading-loose text-center transition duration-100 ease-in-out rounded-full cursor-pointer hover:bg-blue-200"
									>
										{day}
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default DatePicker;
